import {
  getLicenseFlowConfig,
  LicenseActivationMode,
  LicenseStorageKeys,
  LicenseType,
  SignUpFlow,
} from './license-flow.config';

export interface LicenseRouteLike {
  readonly params: Record<string, string | undefined>;
  readonly queryParams?: Record<string, string | undefined>;
}

export interface LegacyLicenseStorage {
  removeItem(key: string): void;
}

export interface LicenseRouteContext {
  readonly type: LicenseType;
  readonly activationMode: LicenseActivationMode;
  readonly licenseId: string;
  readonly email: string;
  readonly signUpFlow: SignUpFlow;
  readonly canonicalUrl: string;
  readonly isUntypedLicenseUrl: boolean;
}

// Partner links sometimes wrap the id in brackets, quotes or whitespace: `/activate/[01M1...`
const licenseIdEdgeNoise = /^[\s[\]{}()<>"'`]+|[\s[\]{}()<>"'`]+$/g;

const legacyLicenseStorageKeys = [
  LicenseStorageKeys.activationPending,
  LicenseStorageKeys.email,
  LicenseStorageKeys.licenseId,
  LicenseStorageKeys.licenseType,
] as const;

export function buildLicenseRouteContext(route: LicenseRouteLike): LicenseRouteContext {
  const config = getLicenseFlowConfig(route.params['type']);

  return {
    type: config.type,
    activationMode: config.activationMode,
    licenseId: config.activationMode === 'with-id' ? sanitizeLicenseId(route.params['id']) : '',
    email: route.queryParams?.['email'] ?? '',
    signUpFlow: config.signUpFlow,
    canonicalUrl: buildCanonicalLicenseUrl(route),
    isUntypedLicenseUrl: !route.params['type'],
  };
}

export function buildCanonicalLicenseUrl(route: LicenseRouteLike): string {
  const type = route.params['type'];
  const id = sanitizeLicenseId(route.params['id']);
  const path = type ? `/license/${type}/activate${id ? `/${id}` : ''}` : `/license/activate/${id}`;
  const query = new URLSearchParams();

  Object.entries(route.queryParams ?? {}).forEach(([key, value]) => {
    if (value) {
      query.set(key, value);
    }
  });

  const queryString = query.toString();

  return queryString ? `${path}?${queryString}` : path;
}

export function sanitizeLicenseId(id: string | null | undefined): string {
  return (id ?? '').replace(licenseIdEdgeNoise, '');
}

export function clearLegacyLicenseStorage(storage: LegacyLicenseStorage): void {
  legacyLicenseStorageKeys.forEach(key => storage.removeItem(key));
}
